import React from 'react'

export type BadgeVariant = 'sky' | 'amber' | 'green' | 'gray' | 'red' | 'blue' | 'ocean' | 'steel'
type BadgeSize = 'sm' | 'md' | 'lg'

// Variants map to the Tellus brand palette (tailwind.config.js), not generic
// tailwind colors, so every badge is on-brand and consistent with the suite.
const variantClasses: Record<BadgeVariant, string> = {
  sky: 'bg-drizzly-day-dark text-perfect-shine-dark border-drizzly-day',
  amber: 'bg-anzac-light text-anzac-dark border-anzac',
  green: 'bg-emerald-light text-emerald-dark border-emerald',
  gray: 'bg-foggy-morning-dark text-pacific-storm-dark border-drizzly-day',
  red: 'bg-cerise-red-light text-cerise-red-dark border-cerise-red',
  blue: 'bg-polo-blue-light text-fine-barrel border-polo-blue',
  // Source-provenance pair for the marketplace tables: location coded by hue so it
  // reads across the room (blue = in our marketplace, amber = external), while
  // neither can be mistaken for the solid brand-blue Express Interest button.
  // `ocean` = our own listings: a LIGHT brand-blue tint whose text, border and dot
  // are the bright brand blue - energetic without going solid, and never the solid
  // Express-Interest button. `steel` = externally-sourced listings: the warm amber
  // counterpoint (anzac), so internal vs external is a clear blue/amber distinction
  // at a glance rather than the old blue-on-gray.
  ocean: 'bg-perfect-shine-100 text-perfect-shine border-perfect-shine-light',
  steel: 'bg-anzac-light text-anzac-dark border-anzac',
}

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'text-[11px] px-2 py-0.5 font-medium',
  md: 'text-sm px-3 py-1 font-medium',
  lg: 'text-base px-4 py-1.5 font-semibold',
}

interface BadgeProps {
  variant: BadgeVariant
  size?: BadgeSize
  count?: number
  children: React.ReactNode
  'data-testid'?: string
}

const Badge = ({ variant, size = 'sm', count, children, 'data-testid': testId }: BadgeProps) => {
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border ${variantClasses[variant]} ${sizeClasses[size]}`}
      data-testid={testId}
    >
      {children}
      {count !== undefined && (
        <span className="bg-cerise-red text-white rounded-full text-xs font-semibold px-1.5 py-0.5 tabular-nums">
          {count}
        </span>
      )}
    </span>
  )
}

export default Badge
